/**
 * Секции графиков по узлам: какая карточка в какую группу и на какую вкладку.
 *
 * Каталог отдаёт плоский список карточек в порядке нод пакета, а карточки разной природы:
 * распределения, чек-листы-таблицы, цитаты. Группа решает заголовок над карточками и вкладку;
 * место и размер карточки на вкладке задаёт сетка (`config/sectionLayouts.js`).
 *
 * Секция берётся из спеки карточки (`nodeChartSpec`), а не из захардкоженного соответствия
 * «тип графика → секция»: одному типу графика может место и в распределениях, и в качестве,
 * а решает это вид выхода.
 */
import { resolveChartSpec, SECTIONS } from './nodeChartSpec';

export const CHART_SECTIONS = [
  {
    id: SECTIONS.Distribution,
    title: 'Распределение звонков',
    hint: 'Как звонки за период распределились по значениям, которые выставили узлы анализа.'
  },
  {
    id: SECTIONS.Quality,
    title: 'Чек-листы качества',
    // Про разный набор операторов сказано здесь, а не в каждой карточке: это свойство
    // всей секции. Чек-лист применяется только к своему типу звонков, поэтому у одного
    // в строках четыре оператора, а у другого один — и без объяснения это читается
    // как сломанная выборка.
    hint:
      'Каждый чек-лист применяется только к своему типу звонков, поэтому набор операторов '
      + 'в них разный: в строках только те, у кого такие звонки были. Клик по ячейке '
      + 'открывает примеры, клик по пункту во вкладке «По пунктам» — журнал звонков.'
  },
  {
    id: SECTIONS.Texts,
    title: 'Цитаты и содержание',
    hint: 'Текстовые выходы узлов: краткое содержание и цитаты из разговоров.'
  }
];

const SECTION_IDS = new Set(CHART_SECTIONS.map((section) => section.id));

/**
 * На какую вкладку дашборда попадает секция. Единственное место, где это записано.
 *
 * Раньше состав секций был захардкожен в самих вкладках — по списку в каждой. Экран настроек
 * обещает пользователю «этот график появится там-то», и обещание должно ломаться сборкой,
 * а не глазами при следующей правке.
 */
export const CHART_TABS = [
  { id: 'general', title: 'Общая статистика', sections: [SECTIONS.Distribution, SECTIONS.Texts] },
  { id: 'employees', title: 'Сотрудники', sections: [SECTIONS.Quality] }
];

/** Секции вкладки по её идентификатору. */
export const resolveTabSections = (tabId) =>
  CHART_TABS.find((tab) => tab.id === tabId)?.sections ?? [];

// Незнакомая секция (например, из будущей версии пакета) не должна ронять карточку
// в никуда — показываем её в распределениях.
const resolveSectionId = (spec) =>
  (SECTION_IDS.has(spec.section) ? spec.section : SECTIONS.Distribution);

const bucketBySection = (prepared) =>
  CHART_SECTIONS.map((section) => ({
    ...section,
    charts: prepared.filter((entry) => resolveSectionId(entry.spec) === section.id)
  })).filter((section) => section.charts.length > 0);

const prepare = (charts) => charts.map((chart) => ({ chart, spec: resolveChartSpec(chart) }));

/**
 * Раскладывает карточки каталога по секциям с сохранением порядка пакета.
 * Каждая карточка отдаётся вместе с готовой спекой, чтобы секция не считала её второй раз.
 *
 * Отсев скрытых здесь — единственный: сервер отдаёт в каталоге все карточки, включая скрытые,
 * потому что экрану настроек нужно показать их для включения обратно.
 */
export const groupChartsBySection = (charts = []) =>
  bucketBySection(prepare(charts).filter((entry) => !entry.spec.hidden));

/** То же самое, но со скрытыми карточками — для экрана настроек. */
export const groupAllChartsBySection = (charts = []) => bucketBySection(prepare(charts));
